// Full content for the new file
import {
  Dialog,
  YStack,
  XStack,
  useMedia,
  useWindowDimensions,
  View,
  isWeb,
} from 'tamagui'
import { Modal } from '@components/Modal'
import { ScrollBar } from '@components/ScrollBar'
import { Button } from '@components/Button'
import { HeroIcon } from '@components/HeroIcon'
import { CONTINUE_CARD_DIMENSIONS } from '@constants/continueCardDimensions'
import { useMediaSize } from '@hooks/useMediaSize'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useMemo } from 'react'
import { Platform } from 'react-native'

interface IExtraContentAllModalProps {
  open: boolean
  onClose: () => void
  cards: React.ReactElement[]
  title: string
}

export function ExtraContentAllModal({
  open,
  onClose,
  cards,
  title,
}: IExtraContentAllModalProps) {
  const media = useMedia()
  const safeArea = useSafeAreaInsets()
  const { height, width } = useWindowDimensions()
  const size = useMediaSize()

  const modalPaddingBottom = media.gtSm ? 10 : 50
  const modalCardsGap = media.gtMd ? 20 : 12
  const calcScrollBarMaxHeight = media.gtSm
    ? height * 0.9
    : height - safeArea.top
  const titlePaddingVertical = media.gtSm
    ? '$size.spacing-md'
    : '$size.spacing-xs'

  const cardsPerRow = useMemo(() => {
    if (media.gtLg) return 4
    if (media.gtMd) return 3
    if (media.gtSm) return 2
    return 1
  }, [media.gtLg, media.gtMd, media.gtSm])

  const modalMaxPosition = useMemo(() => {
    if (isWeb) return undefined
    const number =
      Platform.OS === 'ios'
        ? height - safeArea.top
        : height - (safeArea.top + safeArea.bottom)
    return [number, number]
  }, [height, safeArea.top, safeArea.bottom])

  const calcModalMaxWidth = useMemo(() => {
    if (media.gtLg) return 1448
    if (media.gtMd) return 1100
    if (media.gtSm) return 568
    return width
  }, [media.gtLg, media.gtMd, media.gtSm, width])

  const setContainerWidth = () => {
    const gapTotalSpacing = modalCardsGap * cardsPerRow
    const cardsTotalWidth =
      (CONTINUE_CARD_DIMENSIONS[size].width ||
        CONTINUE_CARD_DIMENSIONS.xs.width) * cardsPerRow
    return cardsTotalWidth + gapTotalSpacing
  }

  return (
    <Modal
      onClose={onClose}
      open={open}
      snapPointsMode={isWeb ? undefined : 'mixed'}
      snapPoints={modalMaxPosition}
      backdropOpacity={0.64}
      shouldAdapt={false}
      maxWidth={calcModalMaxWidth}
      disableDrag
    >
      <YStack
        paddingLeft="$size.spacing-xs"
        height={calcScrollBarMaxHeight}
        $gtSm={{
          padding: '$size.spacing-lg',
        }}
        flex={1}
      >
        <View
          paddingVertical={titlePaddingVertical}
          flexDirection="row"
          justifyContent="space-between"
          $gtMd={{
            paddingTop: 0,
          }}
        >
          <Dialog.Title
            color="$text-primary"
            ff="$mulishSemiBold"
            fontSize="$font-size-sm"
          >
            {title}
          </Dialog.Title>
          {media.sm && (
            <Button onPress={onClose} icon>
              <HeroIcon
                icon={['fal', 'xmark']}
                color="$icon-primary"
                size={12}
              />
            </Button>
          )}
        </View>
        <ScrollBar
          maxHeight={calcScrollBarMaxHeight}
          displayNativeIndicator={false}
          scrollBarId="extra-content-all-modal"
        >
          <XStack
            key={`${title}-${cards.length}`}
            flexWrap="wrap"
            flex={1}
            gap={modalCardsGap}
            paddingTop="$spacing-inset-xxs"
            paddingBottom={modalPaddingBottom}
            $gtLg={{
              width: setContainerWidth(),
            }}
          >
            {cards}
          </XStack>
        </ScrollBar>
      </YStack>
    </Modal>
  )
}
